import type { PipeTransform } from '@angular/core'
import { Pipe, inject } from '@angular/core'
import type { SafeHtml } from '@angular/platform-browser'
import { DomSanitizer } from '@angular/platform-browser'

import { isSafeHtmlObject } from '../utils/html-utils'

@Pipe({
  name: 'htmlContent',
  pure: false,
})

/**
 * Pipe to render trusted HTML content
 *
 * This pipe takes an HTML string or SafeHtml input from a trusted source and
 * returns a SafeHtml object that can be used with Angular's [innerHTML]
 * binding.
 *
 * It does NOT sanitize the content that it is passed. We use this so that
 * inline styles still work, but it poses a security risk, because any script
 * and style elements will be executed as well.
 *
 * Usage: {{ htmlString | htmlContent }}
 *
 * Note: This pipe is marked as impure (pure: false) to allow updates when the
 * input content changes.
 */
export class HtmlContentPipe implements PipeTransform {
  private sanitizer = inject(DomSanitizer)

  // Cache the last result so unchanged content doesn't produce a new SafeHtml
  // reference every change-detection cycle. A new reference forces Angular to
  // rewrite the bound innerHTML, which clears any active text selection.
  private lastContentString?: string
  private lastResult?: SafeHtml

  transform(content: string | SafeHtml | null | undefined): SafeHtml {
    if (!content) {
      return this.sanitizer.bypassSecurityTrustHtml('')
    }

    try {
      // If it's already SafeHtml, return it as is
      if (isSafeHtmlObject(content)) {
        return content
      }

      // Convert to string if needed
      const contentString =
        typeof content === 'string' ? content : content.toString()

      if (
        this.lastResult !== undefined &&
        contentString === this.lastContentString
      ) {
        return this.lastResult
      }

      // TODO for complete safety, we might want to pass content through DOMpurify or similar
      const result = this.sanitizer.bypassSecurityTrustHtml(contentString)
      this.lastContentString = contentString
      this.lastResult = result
      return result
    } catch (error) {
      console.error('Error in htmlContent pipe:', error)
      return this.sanitizer.bypassSecurityTrustHtml('')
    }
  }
}
